import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PauseCircleOutline as DeactivateIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import DashboardLayout from '../components/DashboardLayout';
import EmployeeFilter from '../components/EmployeeFilter';
import { useAppSelector } from '../store';
import { useEmployeeRecords } from '../hooks/useEmployeeRecords';
import { apiService } from '../services/api';
import { showNotification } from '../store/notificationSlice';
import { useAppDispatch } from '../store';
import type { ManualTimekeepingEntry as TKEntry } from '../types/timekeeping';
import type { EmployeeListItem } from '../types/employees';

// ── Colour tokens ──────────────────────────────────────────────────────────────
const COLOR = {
  // Saved — dark solid background, white text
  activeMachine:      { bg: '#43a047', border: '#2e7d32', text: '#ffffff' },
  deactivatedMachine: { bg: '#757575', border: '#424242', text: '#ffffff' },
  existingManual:     { bg: '#e53935', border: '#b71c1c', text: '#ffffff' },
  // Pending change — light background, dark text
  newManual:          { bg: '#fff8e1', border: '#f9a825', text: '#e65100' },
  bulkManual:         { bg: '#ede7f6', border: '#7e57c2', text: '#4527a0' },
  pendingRemove:      { bg: '#fbe9e7', border: '#ff5722', text: '#bf360c' },
  pendingReactivate:  { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
} as const;

const LEGEND_LABELS: Record<string, string> = {
  activeMachine:      'Machine Entry',
  deactivatedMachine: 'Deactivated',
  existingManual:     'Saved Manual Entry',
  newManual:          'New Manual Entry',
  bulkManual:         'Bulk Add',
  pendingRemove:      'Pending Removal',
  pendingReactivate:  'Pending Activation',
};

// ── Types ──────────────────────────────────────────────────────────────────────

type EntryState = 'activeMachine' | 'deactivatedMachine' | 'existingManual' | 'newManual' | 'bulkManual' | 'pendingRemove' | 'pendingReactivate';

interface DisplayEntry extends TKEntry {
  state: EntryState;
  isNew?: boolean;
  isFromAddAll?: boolean;
  pendingRemove?: boolean;
  pendingReactivate?: boolean;
}

interface EmployeeRow {
  employeeInternalId: string;
  employeeName: string;
  biometricId: string;
  entries: DisplayEntry[];
}

// ── Helper ────────────────────────────────────────────────────────────────────

function entryState(e: TKEntry): EntryState {
  if (e.entryType === 'Machine') return e.isDeactivated ? 'deactivatedMachine' : 'activeMachine';
  return 'existingManual';
}

function entryColor(state: EntryState) {
  return COLOR[state];
}

function isDirty(rows: EmployeeRow[]): boolean {
  return rows.some((r) =>
    r.entries.some((e) => e.isNew || e.pendingRemove || e.pendingReactivate),
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ManualTimekeepingEntry() {
  const dispatch = useAppDispatch();
  const selectedUnit = useAppSelector((s) => s.auth.selectedUnit);
  const uiFields = useAppSelector((s) => s.applicationSettings.uiFields);
  const dbKey = selectedUnit
    ? `cranberry-employees-${selectedUnit.tenant}-${selectedUnit.solution}-${selectedUnit.unit}`
    : '';

  const employeeRecords = useEmployeeRecords(dbKey);
  const employeeList = useAppSelector((s) => s.employees.list);

  const PAGE_SIZE = 200;

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [filteredUUIDs, setFilteredUUIDs] = useState<string[] | null>(null);
  const [rows, setRows] = useState<EmployeeRow[]>([]);
  const [page, setPage] = useState(0);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Panel resize ─────────────────────────────────────────────────────────────
  const MIN_PANEL_WIDTH = 450;
  const [panelWidth, setPanelWidth] = useState(MIN_PANEL_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const handleResizeStart = (e: React.MouseEvent) => {
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const newWidth = Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + (e.clientX - dragStartX.current));
      setPanelWidth(newWidth);
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  // Pending-navigation state for unsaved-changes guard
  const pendingAction = useRef<(() => void) | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Add-time dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addTarget, setAddTarget] = useState<string[]>([]);
  const [isAddAllOp, setIsAddAllOp] = useState(false);
  const [hasAddedAll, setHasAddedAll] = useState(false);
  const [addTimeInput, setAddTimeInput] = useState<Dayjs | null>(null);
  const [addRangeFrom, setAddRangeFrom] = useState<Dayjs | null>(null);
  const [addRangeTo, setAddRangeTo] = useState<Dayjs | null>(null);
  const [addMode, setAddMode] = useState<'fixed' | 'range'>('fixed');

  // ── Guard helper ─────────────────────────────────────────────────────────────

  const guardedAction = useCallback((action: () => void) => {
    if (isDirty(rows)) {
      pendingAction.current = action;
      setConfirmOpen(true);
    } else {
      action();
    }
  }, [rows]);

  const confirmDiscard = () => {
    setConfirmOpen(false);
    setRows([]);
    pendingAction.current?.();
    pendingAction.current = null;
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────────

  const fetchEntries = useCallback(async (date: Dayjs, uuids: string[] | null, pageNum: number) => {
    const candidates: EmployeeListItem[] = uuids
      ? employeeList.filter((e) => uuids.includes(e.employeeUUID))
      : employeeList;

    const withBio = candidates.filter((e) => e.biometricId);
    setTotalFilteredCount(withBio.length);

    const pageStart = pageNum * PAGE_SIZE;
    const pageEmployees = withBio.slice(pageStart, pageStart + PAGE_SIZE);

    if (pageEmployees.length === 0) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const res = await apiService.queryManualTimekeepingEntries({
        date: date.format('YYYY-MM-DD'),
        employees: pageEmployees.map((e) => ({
          employeeInternalId: e.employeeInternalId,
          biometricId: e.biometricId,
        })),
      });

      const dataMap: Record<string, TKEntry[]> = res.data?.data ?? {};
      const newRows: EmployeeRow[] = pageEmployees.map((emp) => ({
        employeeInternalId: emp.employeeInternalId,
        employeeName: emp.employeeName,
        biometricId: emp.biometricId,
        entries: (dataMap[emp.employeeInternalId] ?? []).map((e) => ({
          ...e,
          state: entryState(e),
        })),
      }));
      setRows(newRows);
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to load timekeeping data', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [employeeList, dispatch, PAGE_SIZE]);

  // ── Date change ───────────────────────────────────────────────────────────────

  const handleDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
  };

  // ── Filter change ─────────────────────────────────────────────────────────────

  const handleFilterChange = (uuids: string[] | null) => {
    setFilteredUUIDs(uuids);
  };

  // ── Search ────────────────────────────────────────────────────────────────────

  const handleSearch = () => {
    if (!selectedDate?.isValid() || !filteredUUIDs) return;
    guardedAction(() => {
      setPage(0);
      setSearched(true);
      fetchEntries(selectedDate, filteredUUIDs, 0);
    });
  };

  // ── Page change ───────────────────────────────────────────────────────────────

  const handlePageChange = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    if (!selectedDate?.isValid()) return;
    setPage(newPage);
    fetchEntries(selectedDate, filteredUUIDs, newPage);
  };

  // ── Entry mutations (local state) ─────────────────────────────────────────────

  const togglePendingRemove = (empId: string, timestamp: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.employeeInternalId !== empId) return row;
        return {
          ...row,
          entries: row.entries.map((e) => {
            if (e.timestamp !== timestamp) return e;
            if (e.isNew) {
              // Remove unsaved additions immediately
              return null as unknown as DisplayEntry;
            }
            if (e.isDeactivated) {
              const nowPending = !e.pendingReactivate;
              return {
                ...e,
                pendingReactivate: nowPending,
                state: (nowPending ? 'pendingReactivate' : 'deactivatedMachine') as EntryState,
              };
            }
            const nowPending = !e.pendingRemove;
            return {
              ...e,
              pendingRemove: nowPending,
              state: (nowPending ? 'pendingRemove' : entryState(e)) as EntryState,
            };
          }).filter(Boolean),
        };
      }),
    );
  };

  // ── Add-time dialog ───────────────────────────────────────────────────────────

  const openAddDialog = (targets: string[]) => {
    setAddTarget(targets);
    setAddTimeInput(null);
    setAddRangeFrom(null);
    setAddRangeTo(null);
    setAddMode('fixed');
    setAddDialogOpen(true);
  };

  function randomTimeInRange(from: string, to: string): string {
    const toSeconds = (t: string) => {
      const [h, m, s] = t.split(':').map(Number);
      return h * 3600 + m * 60 + (s || 0);
    };
    const fromSec = toSeconds(from);
    const toSec = toSeconds(to);
    if (fromSec >= toSec) return from;
    const rand = Math.floor(Math.random() * (toSec - fromSec + 1)) + fromSec;
    const h = Math.floor(rand / 3600).toString().padStart(2, '0');
    const m = Math.floor((rand % 3600) / 60).toString().padStart(2, '0');
    const s = (rand % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  const handleAddConfirm = () => {
    setAddDialogOpen(false);
    const now = Date.now();
    setRows((prev) =>
      prev.map((row) => {
        if (!addTarget.includes(row.employeeInternalId)) return row;
        const time = addMode === 'fixed'
          ? (addTimeInput?.format('HH:mm:ss') ?? '')
          : randomTimeInRange(
              addRangeFrom?.format('HH:mm:ss') ?? '',
              addRangeTo?.format('HH:mm:ss') ?? '',
            );
        if (!time) return row;
        // On override, strip the previous Add-All entries first
        const baseEntries = isAddAllOp
          ? row.entries.filter((e) => !e.isFromAddAll)
          : row.entries;
        const newEntry: DisplayEntry = {
          time,
          entryType: 'Manual',
          isDeactivated: false,
          timestamp: `new-${now}-${row.employeeInternalId}`,
          state: isAddAllOp ? 'bulkManual' : 'newManual',
          isNew: true,
          isFromAddAll: isAddAllOp,
        };
        return { ...row, entries: [...baseEntries, newEntry] };
      }),
    );
    if (isAddAllOp) setHasAddedAll(true);
  };

  const handleResetAll = () => {
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        entries: row.entries.filter((e) => !e.isFromAddAll),
      })),
    );
    setHasAddedAll(false);
  };

  // ── Save ──────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedDate?.isValid()) return;
    setSaving(true);
    try {
      const data: Record<string, { biometricId: string; add: string[]; remove: string[]; reactivate: string[] }> = {};
      for (const row of rows) {
        const add = row.entries.filter((e) => e.isNew).map((e) => e.time);
        const remove = row.entries.filter((e) => e.pendingRemove && !e.isNew).map((e) => e.timestamp);
        const reactivate = row.entries.filter((e) => e.pendingReactivate && !e.isNew).map((e) => e.timestamp);
        if (add.length === 0 && remove.length === 0 && reactivate.length === 0) continue;
        data[row.employeeInternalId] = { biometricId: row.biometricId, add, remove, reactivate };
      }
      await apiService.batchManualTimekeepingEntries({ date: selectedDate.format('YYYY-MM-DD'), data });
      dispatch(showNotification({ message: 'Changes saved successfully.', severity: 'success' }));
      setHasAddedAll(false);
      fetchEntries(selectedDate, filteredUUIDs, page);
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to save changes', severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const dirty = isDirty(rows);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', m: -3, minHeight: 'calc(100vh - 64px)', ...(isDragging && { cursor: 'col-resize', userSelect: 'none' }) }}>

        {/* Page header */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Manual Timekeeping Entry
          </Typography>
        </Box>

        {/* Two-panel body */}
        <Box sx={{ display: 'flex', flexGrow: 1, gap: 2, overflow: 'hidden', px: 3, pb: 2 }}>

          {/* ── Left panel: Date + Filter ── */}
          <Paper
            variant="outlined"
            sx={{
              width: panelWidth,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 2,
              borderRadius: 1,
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />

            {uiFields.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>
                  Filter
                </Typography>
                <EmployeeFilter
                  uiFields={uiFields}
                  employees={employeeRecords}
                  onChange={handleFilterChange}
                  onSearch={handleSearch}
                  searchDisabled={!selectedDate?.isValid() || !filteredUUIDs}
                />
              </Box>
            )}

            {/* Resize handle */}
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                position: 'absolute',
                top: 0,
                right: -4,
                width: 8,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover > span, &:active > span': { opacity: 1 },
              }}
            >
              <Box component="span" sx={{
                width: 3,
                height: 40,
                borderRadius: 2,
                bgcolor: 'divider',
                opacity: 0,
                transition: 'opacity 0.15s',
              }} />
            </Box>
          </Paper>

          {/* ── Right panel: Table ── */}
          <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Toolbar: legend + bulk buttons */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>

              {/* Legend */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>
                  Time Entry Color Code
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {(Object.entries(COLOR) as [EntryState, typeof COLOR[EntryState]][]).map(([key, c]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: c.bg, border: `1.5px solid ${c.border}`, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                        {LEGEND_LABELS[key] ?? key}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {rows.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setIsAddAllOp(true);
                      openAddDialog(rows.map((r) => r.employeeInternalId));
                    }}
                  >
                    {hasAddedAll ? 'Bulk Add (Override)' : 'Bulk Add'}
                  </Button>
                  {hasAddedAll && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      onClick={handleResetAll}
                    >
                      Reset
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* Loading */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Pre-search prompt */}
            {!loading && !searched && (
              <Alert severity="info" sx={{ maxWidth: 480 }}>
                Apply a filter and click <strong>Search</strong> to load timekeeping records.
              </Alert>
            )}

            {/* Table */}
            {!loading && rows.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, width: 130 }}>Employee ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 180 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Time Entries</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80 }} align="right">Add</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.employeeInternalId} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                          {row.employeeInternalId}
                        </TableCell>
                        <TableCell>{row.employeeName}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {row.entries.map((entry) => {
                              const c = entryColor(entry.state);
                              return (
                                <Tooltip
                                  key={entry.timestamp}
                                  title={
                                    entry.pendingRemove || entry.pendingReactivate
                                      ? 'Click to undo'
                                      : entry.isDeactivated
                                      ? 'Click to activate'
                                      : entry.entryType === 'Machine'
                                      ? 'Click to deactivate'
                                      : 'Click to delete'
                                  }
                                >
                                  <Chip
                                    label={entry.time}
                                    size="small"
                                    deleteIcon={
                                      entry.pendingRemove || entry.pendingReactivate
                                        ? <DeleteIcon fontSize="small" />
                                        : entry.isDeactivated
                                        ? <AddIcon fontSize="small" />
                                        : entry.entryType === 'Machine'
                                        ? <DeactivateIcon fontSize="small" />
                                        : <DeleteIcon fontSize="small" />
                                    }
                                    onDelete={() => togglePendingRemove(row.employeeInternalId, entry.timestamp)}
                                    sx={{
                                      bgcolor: c.bg,
                                      border: `1px solid ${c.border}`,
                                      color: c.text,
                                      fontFamily: 'monospace',
                                      fontSize: 12,
                                      textDecoration: entry.pendingRemove ? 'line-through' : 'none',
                                      '& .MuiChip-deleteIcon': { color: c.text, opacity: 0.7 },
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                            {row.entries.length === 0 && (
                              <Typography variant="caption" color="text.disabled">No entries</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Add time entry for this employee">
                            <IconButton size="small" onClick={() => { setIsAddAllOp(false); openAddDialog([row.employeeInternalId]); }}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loading && searched && rows.length === 0 && (
              <Alert severity="info" sx={{ maxWidth: 400 }}>
                No employees with biometric IDs found for the current filter.
              </Alert>
            )}

            {/* Pagination */}
            {!loading && totalFilteredCount > 0 && (
              <TablePagination
                component="div"
                count={totalFilteredCount}
                page={page}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                onPageChange={handlePageChange}
                sx={{ mt: 'auto' }}
              />
            )}
          </Box>
        </Box>

        {/* Sticky save bar */}
        <Paper
          elevation={6}
          sx={{ position: 'sticky', bottom: 0, px: 3, py: 1.5, display: 'flex', gap: 1, borderRadius: 0, mt: 'auto' }}
        >
          <Button
            variant="contained"
            disabled={!dirty || saving}
            onClick={handleSave}
          >
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Save
          </Button>
          <Button
            variant="outlined"
            disabled={!dirty || saving}
            onClick={() => { if (selectedDate?.isValid()) { setHasAddedAll(false); fetchEntries(selectedDate, filteredUUIDs, page); } }}
          >
            Discard
          </Button>
        </Paper>
      </Box>

      {/* Unsaved changes confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>You have unsaved changes. They will be lost if you continue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDiscard} color="error" variant="contained">Discard & Continue</Button>
        </DialogActions>
      </Dialog>

      {/* Add-time dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick') setAddDialogOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Add Time Entry
          {addTarget.length > 1 && (
            <Typography variant="caption" display="block" color="text.secondary">
              Adding to {addTarget.length} employees
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <Button
              size="small"
              variant={addMode === 'fixed' ? 'contained' : 'outlined'}
              onClick={() => {
                if (addMode === 'range' && addRangeFrom) setAddTimeInput(addRangeFrom);
                setAddMode('fixed');
              }}
            >
              Fixed Time
            </Button>
            <Button
              size="small"
              variant={addMode === 'range' ? 'contained' : 'outlined'}
              onClick={() => {
                if (addMode === 'fixed' && addTimeInput) setAddRangeFrom(addTimeInput);
                setAddMode('range');
              }}
            >
              Random Range
            </Button>
          </Box>

          {addMode === 'fixed' ? (
            <TimePicker
              label="Time"
              value={addTimeInput}
              onChange={(v) => setAddTimeInput(v)}
              views={['hours', 'minutes', 'seconds']}
              format="HH:mm:ss"
              ampm={false}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TimePicker
                label="From"
                value={addRangeFrom}
                onChange={(v) => setAddRangeFrom(v)}
                views={['hours', 'minutes', 'seconds']}
                format="HH:mm:ss"
                ampm={false}
                slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
              />
              <TimePicker
                label="To"
                value={addRangeTo}
                onChange={(v) => setAddRangeTo(v)}
                views={['hours', 'minutes', 'seconds']}
                format="HH:mm:ss"
                ampm={false}
                slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
              />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The server timezone will be applied to this time when saving.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddConfirm}
            disabled={addMode === 'fixed' ? !addTimeInput?.isValid() : !addRangeFrom?.isValid() || !addRangeTo?.isValid()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
